'use client'
import type { ReactNode } from 'react'
import { HStack, Switch, type SwitchProps } from '@chakra-ui/react'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import {
  FormDescription,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  FormMessage
} from '../FormField'

interface GrandFormSwitchFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends Omit<SwitchProps, 'name' | 'isChecked' | 'onChange' | 'value' | 'defaultChecked'> {
  name: TName
  label?: string
  description?: string
  tooltip?: ReactNode
  switchText?: string
  required?: boolean
  optional?: boolean
  disabled?: boolean
  rules?: RegisterOptions<TFieldValues, TName>
  control?: Control<TFieldValues>
}

export const GrandFormSwitchField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  description,
  tooltip,
  switchText,
  required,
  optional,
  disabled,
  rules,
  control,
  ...switchProps
}: GrandFormSwitchFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      optional={optional}
      required={required}
      rules={rules}
      render={({ field: { value, onChange, onBlur, ref } }) => (
        <FormFieldControl disabled={disabled}>
          <HStack align="center" spacing={3}>
            <Switch
              isChecked={!!value}
              isDisabled={disabled}
              onBlur={onBlur}
              onChange={(e) => onChange(e.target.checked)}
              ref={ref}
              {...switchProps}
            />

            {label || switchText ? (
              <FormFieldLabel mb={0} tooltip={tooltip}>
                {label ?? switchText}
              </FormFieldLabel>
            ) : null}
          </HStack>

          {description ? <FormDescription>{description}</FormDescription> : null}

          <FormMessage />
        </FormFieldControl>
      )}
    />
  )
}
